type TickListener = () => void;

type TickerBucket = {
  intervalId: number;
  listeners: Set<TickListener>;
};

const buckets = new Map<number, TickerBucket>();

// Architectural summary: Provides one shared setInterval per cadence so multiple
// subscribers can reuse a single timer and avoid redundant wakeups/rerenders.
export function subscribeSharedTicker(intervalMs: number, listener: TickListener): () => void {
  if (typeof window === 'undefined') {
    return () => {
      // Noop in non-browser environments.
    };
  }

  const safeInterval = Math.max(1000, Math.trunc(intervalMs));
  let bucket = buckets.get(safeInterval);

  if (!bucket) {
    const listeners = new Set<TickListener>();
    const intervalId = window.setInterval(() => {
      const active = buckets.get(safeInterval);
      if (!active) {
        return;
      }
      active.listeners.forEach((cb) => cb());
    }, safeInterval);

    bucket = { intervalId, listeners };
    buckets.set(safeInterval, bucket);
  }

  bucket.listeners.add(listener);

  return () => {
    const active = buckets.get(safeInterval);
    if (!active) {
      return;
    }

    active.listeners.delete(listener);
    if (active.listeners.size === 0) {
      window.clearInterval(active.intervalId);
      buckets.delete(safeInterval);
    }
  };
}
